import React, { useEffect, useState } from 'react';
import { Alert, Button, Divider, Radio, Space, Spin, Tag, Typography } from 'antd';
import {
  CheckCircleOutlined,
  DownloadOutlined,
  QuestionCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { downloadFile, viewPatientFile } from '../../../../api/file/fileServiceApi';
import type { UserUuid } from '../../../../types/identity';

const { Text } = Typography;

/**
 * Clinical-side, READ-ONLY Certificate-of-Analysis AI analysis panel.
 *
 * Ported from the admin actionable-frontend CoaAnalysisPanel. The compute now runs
 * clinically (AnalyseCoaJob → CoaAnalysisService) and the result is stored on the CoA
 * upload stage-log's outcome_data.ai_coa_analysis. This panel only displays it — it
 * never triggers analysis, approves, or rejects (that is the practitioner's decision,
 * enforced by the coa_analysis_passed condition + prescribe gate).
 *
 * Unlike the admin panel it distinguishes MANDATORY-flagged sections (which block the
 * workflow) from advisory flags.
 */

export type CoaCheckResult = {
  status: 'pass' | 'flag' | 'uncertain';
  note?: string;
};

export type CoaAnalysis = {
  status: 'analysed' | 'no_coa' | 'error';
  message?: string;
  identity_and_purity?: CoaCheckResult;
  sterility?: CoaCheckResult;
  bacterial_endotoxin?: CoaCheckResult;
  pharmacy_licence?: CoaCheckResult;
  batch_number?: CoaCheckResult;
  expiry?: CoaCheckResult;
  provenance_pharmacy_named?: CoaCheckResult;
  provenance_batch_reference?: CoaCheckResult;
  provenance_origin_clarity?: CoaCheckResult;
  overall_summary?: string;
  passed?: boolean;
  mandatory_sections?: string[];
  flagged_mandatory_sections?: string[];
};

type CoaSection = { key: keyof CoaAnalysis; label: string };

const CONTENT_SECTIONS: CoaSection[] = [
  { key: 'identity_and_purity', label: 'Identity & Purity (HPLC/MS)' },
  { key: 'sterility', label: 'Sterility (Ph. Eur. 2.6.1 / TGO 77)' },
  { key: 'bacterial_endotoxin', label: 'Bacterial Endotoxin (Ph. Eur. 2.6.14)' },
  { key: 'pharmacy_licence', label: 'Pharmacy Licence & Sterile Compounding Endorsement' },
  { key: 'batch_number', label: 'Batch Number' },
  { key: 'expiry', label: 'Expiry / Use-by Date' },
];

const PROVENANCE_SECTIONS: CoaSection[] = [
  { key: 'provenance_pharmacy_named', label: 'Pharmacy / Manufacturer Named as Source' },
  { key: 'provenance_batch_reference', label: 'Batch Reference Present' },
  { key: 'provenance_origin_clarity', label: 'Origin Clarity' },
];

const checkIcon = (status: CoaCheckResult['status'], mandatory: boolean) => {
  if (status === 'pass') return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
  if (status === 'flag') {
    return <WarningOutlined style={{ color: mandatory ? '#ff4d4f' : '#faad14' }} />;
  }
  return <QuestionCircleOutlined style={{ color: '#faad14' }} />;
};

const checkTag = (status: CoaCheckResult['status'], mandatory: boolean) => {
  if (status === 'pass') return <Tag color="success">Pass</Tag>;
  if (status === 'flag') {
    return mandatory
      ? <Tag color="error">Flag — mandatory</Tag>
      : <Tag color="warning">Flag — advisory</Tag>;
  }
  return <Tag color="warning">Uncertain</Tag>;
};

type CoaCheckRowProps = { label: string; check?: CoaCheckResult; mandatory: boolean };

const CoaCheckRow = ({ label, check, mandatory }: CoaCheckRowProps) => {
  if (!check) return null;
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8 }}>
      <span style={{ marginTop: 2 }}>{checkIcon(check.status, mandatory)}</span>
      <div style={{ flex: 1 }}>
        <Space>
          <Text strong>{label}</Text>
          {checkTag(check.status, mandatory)}
        </Space>
        {check.note && (
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>{check.note}</Text>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Inline viewer for the uploaded certificate(s), rendered beside the check rows so the
 * practitioner can compare the certificate against the notes. Native <iframe> for PDFs (and
 * anything else the browser can embed), <img> for image uploads — no PDF-rendering library
 * (Harry confirmed the native browser viewer is fine for now — item 3, comment 2026-07-30).
 * A file that fails to load shows an inline message plus the download fallback; it never
 * blanks the check rows next to it.
 */
type CoaDocumentPaneProps = { fileIds: string[]; patientId: UserUuid };

const CoaDocumentPane = ({ fileIds, patientId }: CoaDocumentPaneProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [url, setUrl] = useState<string | null>(null);
  const [contentType, setContentType] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const fileId = fileIds[selectedIndex];

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    setLoading(true);
    setError(null);
    setUrl(null);
    setContentType(null);

    viewPatientFile(patientId, fileId)
      .then((result) => {
        if (cancelled) {
          window.URL.revokeObjectURL(result.url);
          return;
        }
        objectUrl = result.url;
        setUrl(result.url);
        setContentType(result.contentType);
      })
      .catch((err) => {
        console.error('Error loading CoA document:', err, 'fileId:', fileId);
        if (!cancelled) {
          setError('Unable to load the uploaded certificate.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    // Revoke whichever object URL THIS effect run created — on unmount, or before the next
    // run (selection change) — never the stale one from a previous render's closure.
    return () => {
      cancelled = true;
      if (objectUrl) {
        window.URL.revokeObjectURL(objectUrl);
      }
    };
  }, [fileId, patientId]);

  const handleDownload = () => {
    setDownloadError(null);
    downloadFile(patientId, fileId).catch((err) => {
      console.error('Error downloading CoA document:', err, 'fileId:', fileId);
      setDownloadError('Download failed — please try again.');
    });
  };

  const isImage = contentType?.startsWith('image/');

  return (
    <div style={{ flex: '1 1 360px', minWidth: 280 }} data-testid="treatment-plan-coa-document-pane">
      <Divider orientationMargin={0} plain style={{ fontSize: 12, color: '#595959', margin: '8px 0' }}>
        Uploaded certificate
      </Divider>

      <Space style={{ marginBottom: 8 }} wrap>
        {fileIds.length > 1 && (
          <Radio.Group
            size="small"
            value={selectedIndex}
            onChange={(e) => setSelectedIndex(e.target.value)}
            data-testid="treatment-plan-coa-document-switcher"
          >
            {fileIds.map((id, index) => (
              <Radio.Button key={id} value={index}>
                File {index + 1}
              </Radio.Button>
            ))}
          </Radio.Group>
        )}
        <Button
          size="small"
          icon={<DownloadOutlined />}
          onClick={handleDownload}
          data-testid="treatment-plan-coa-document-download"
        >
          Download
        </Button>
      </Space>

      {downloadError && (
        <Text type="danger" style={{ display: 'block', fontSize: 11, marginBottom: 8 }}>
          {downloadError}
        </Text>
      )}

      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
          <Spin />
        </div>
      )}

      {!loading && error && (
        <Alert
          type="warning"
          showIcon
          message="Certificate preview unavailable"
          description={error}
          data-testid="treatment-plan-coa-document-error"
        />
      )}

      {!loading && !error && url && (
        isImage ? (
          <img
            src={url}
            alt="Uploaded certificate of analysis"
            style={{
              width: '100%',
              maxHeight: 520,
              objectFit: 'contain',
              border: '1px solid var(--mg-border, #d9d9d9)',
              borderRadius: 6,
            }}
            data-testid="treatment-plan-coa-document-image"
          />
        ) : (
          <iframe
            src={url}
            title="Uploaded certificate of analysis"
            style={{ width: '100%', height: 520, border: '1px solid var(--mg-border, #d9d9d9)', borderRadius: 6 }}
            data-testid="treatment-plan-coa-document-iframe"
          />
        )
      )}
    </div>
  );
};

type CoaAnalysisPanelProps = {
  analysis?: CoaAnalysis;
  /** True once the CoA files are uploaded — drives the in-progress state before the result lands. */
  hasCoaFile?: boolean;
  /** Uploaded file ids for the certificate — read from the same stage-log as `analysis`. */
  fileIds?: string[];
  /** Owning patient — required alongside `fileIds` to fetch the document inline. */
  patientId?: UserUuid;
};

const CoaAnalysisPanel = ({ analysis, hasCoaFile, fileIds, patientId }: CoaAnalysisPanelProps) => {
  if (!analysis) {
    if (hasCoaFile) {
      return (
        <Alert
          type="info"
          showIcon
          icon={<Spin size="small" />}
          message="AI CoA Analysis"
          description="AI analysis in progress… refresh in a moment to see the result."
          style={{ marginTop: 16 }}
          data-testid="treatment-plan-coa-analysis-pending"
        />
      );
    }
    return null;
  }

  if (analysis.status === 'no_coa') {
    return (
      <Alert
        type="info"
        showIcon
        message="AI CoA Analysis"
        description={analysis.message ?? 'No certificate of analysis was provided.'}
        style={{ marginTop: 16 }}
        data-testid="treatment-plan-coa-analysis-no-coa"
      />
    );
  }

  if (analysis.status === 'error') {
    return (
      <Alert
        type="warning"
        showIcon
        message="AI CoA Analysis unavailable"
        description={analysis.message ?? 'Analysis could not be completed — treated as not cleared.'}
        style={{ marginTop: 16 }}
        data-testid="treatment-plan-coa-analysis-error"
      />
    );
  }

  // status === 'analysed'
  const mandatorySet = new Set(analysis.mandatory_sections ?? []);
  const isMandatory = (key: keyof CoaAnalysis) => mandatorySet.has(key as string);

  const flaggedMandatory = analysis.flagged_mandatory_sections ?? [];
  const mandatoryFlagged = flaggedMandatory.length > 0;

  const renderRows = (sections: CoaSection[]) =>
    sections.map((s) => (
      <CoaCheckRow
        key={s.key as string}
        label={s.label}
        check={analysis[s.key] as CoaCheckResult | undefined}
        mandatory={isMandatory(s.key)}
      />
    ));

  const analysisPanel = (
    <div
      style={{
        marginTop: 16,
        padding: '12px 16px',
        border: '1px solid var(--mg-border, #d9d9d9)',
        borderRadius: 6,
        background: 'var(--mg-surface-muted, #fafafa)',
      }}
      data-testid="treatment-plan-coa-analysis-panel"
    >
      <Space style={{ marginBottom: 8 }}>
        <Text strong>AI CoA Analysis</Text>
        {mandatoryFlagged ? (
          <Tag color="error" icon={<WarningOutlined />}>
            Mandatory section flagged — remediation required
          </Tag>
        ) : (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            All mandatory sections clear
          </Tag>
        )}
      </Space>
      <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 12 }}>
        AI-assisted only — the prescribing decision and gate are deterministic and practitioner-owned.
      </Text>

      <Divider orientationMargin={0} plain style={{ fontSize: 12, color: '#595959', margin: '8px 0' }}>
        Content checks
      </Divider>
      {renderRows(CONTENT_SECTIONS)}

      <Divider orientationMargin={0} plain style={{ fontSize: 12, color: '#595959', margin: '8px 0' }}>
        Provenance checks
      </Divider>
      {renderRows(PROVENANCE_SECTIONS)}

      {analysis.overall_summary && (
        <>
          <Divider orientationMargin={0} plain style={{ fontSize: 12, color: '#595959', margin: '8px 0' }}>
            Summary
          </Divider>
          <Text style={{ fontSize: 13 }}>{analysis.overall_summary}</Text>
        </>
      )}
    </div>
  );

  // Beside the check rows, not instead of them — a document load failure must never blank this.
  if (!fileIds || fileIds.length === 0 || patientId === undefined) {
    return analysisPanel;
  }

  return (
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }} data-testid="treatment-plan-coa-analysis-with-document">
      <div style={{ flex: '1 1 360px', minWidth: 280 }}>{analysisPanel}</div>
      <CoaDocumentPane fileIds={fileIds} patientId={patientId} key={fileIds.join(',')} />
    </div>
  );
};

export default CoaAnalysisPanel;
